import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  getEquipmentDetails,
  selectEquipmentDetails,
  selectEquipmentDetailsLoading,
  selectDeactivateEquipmentLoading,
  selectDeleteEquipmentLoading,
  selectEquipmentDeactivationDetails,
  selectEquipmentDeactivationDetailsLoading,
  selectEquipmentDeletionDetails,
  selectEquipmentDeletionDetailsLoading,
  cleanEquipmentDetails
} from "store/boats"

/**
 * Maps API upcoming bookings to component structure
 * Groups bookings by date
 * @param {Array} apiBookings - Raw bookings from API
 * @returns {Array} Grouped bookings by date
 */
const mapUpcomingBookings = (apiBookings) => {
  if (!apiBookings || apiBookings.length === 0) return []

  // Group bookings by date
  const groupedByDate = apiBookings.reduce((acc, booking) => {
    const date = new Date(booking.booked_date).toLocaleDateString("en-US", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric"
    })

    if (!acc[date]) {
      acc[date] = []
    }

    const isMultiDay = booking.duration_type === "multi_day"
    const timeDisplay = isMultiDay
      ? (booking.booked_time || "").split(" - ")[0].trim() || booking.booked_time
      : booking.booked_time

    acc[date].push({
      id: booking.id,
      experienceName: booking.trip_title,
      time: timeDisplay,
      bookingId: booking.booking_number,
      clientName: booking.client_name,
      crewMembers: booking.crew_count || 0,
      equipment: booking.assets_used?.length || 0,
      paymentStatus: booking.payment_status === "partial" ? "Partial" : "Paid",
      booking_uuid: booking.uuid
    })

    return acc
  }, {})

  // Convert to array format expected by component
  return Object.entries(groupedByDate).map(([date, bookings]) => ({
    date,
    bookings
  }))
}

/**
 * Custom hook for equipment details data fetching and mapping
 * @param {string} id - Equipment ID from URL params
 * @returns {object} Equipment data and loading states
 */
export const useEquipmentDetailsData = (id) => {
  const dispatch = useDispatch()

  // Select equipment details from Redux store
  const equipmentDetails = useSelector(selectEquipmentDetails)
  const detailsLoading = useSelector(selectEquipmentDetailsLoading)

  // Select boat data for modal (fetched separately by modal hook)
  const boat = useSelector((state) => state.boats.boat)
  const boatLoading = boat.loading

  // Redux selectors for deactivate/delete operations
  const deactivateLoading = useSelector(selectDeactivateEquipmentLoading)
  const deleteLoading = useSelector(selectDeleteEquipmentLoading)
  const equipmentDeactivationDetails = useSelector(selectEquipmentDeactivationDetails)
  const deactivationDetailsLoading = useSelector(selectEquipmentDeactivationDetailsLoading)
  const equipmentDeletionDetails = useSelector(selectEquipmentDeletionDetails)
  const deletionDetailsLoading = useSelector(selectEquipmentDeletionDetailsLoading)

  // Clear old equipment details when ID changes to prevent showing stale data
  useEffect(() => {
    // Clear previous equipment details to show loading instead of old data
    dispatch(cleanEquipmentDetails())

    // Cleanup on unmount
    return () => {
      dispatch(cleanEquipmentDetails())
    }
  }, [id, dispatch])

  // Fetch equipment details on mount
  useEffect(() => {
    if (id) {
      dispatch(getEquipmentDetails(id))
    }
  }, [dispatch, id])

  // Extract the actual equipment data from the API response wrapper
  const equipmentData = equipmentDetails?.data

  // Map API response to component structure
  const equipment = equipmentData
    ? {
        id: equipmentData.id,
        name: equipmentData.header?.name || equipmentData.equipment_title,
        description: equipmentData.details?.description || equipmentData.description,
        is_active: equipmentData.status === "active",
        max_users: equipmentData.max_guests,
        category: {
          id: equipmentData.category?.id || equipmentData.category_id,
          name: equipmentData.header?.category || equipmentData.category?.name || equipmentData.boat_category?.name
        },
        characteristics: equipmentData.details?.characteristics || [],
        // Combine all media types (photos, videos, cropped_photos, trimmed_videos) into a single array
        media: [
          ...(equipmentData.media_gallery?.photos || []),
          ...(equipmentData.media_gallery?.videos || []),
          ...(equipmentData.media_gallery?.cropped_photos || []),
          ...(equipmentData.media_gallery?.trimmed_videos || [])
        ].sort((a, b) => (a.position || 0) - (b.position || 0)), // Sort by position if available
        trips: equipmentData.assigned_experiences || []
      }
    : null

  // Use API bookings if available, otherwise use empty array
  const upcomingBookings = equipmentData?.upcoming_bookings ? mapUpcomingBookings(equipmentData.upcoming_bookings) : []

  // Use metadata count if available, otherwise calculate from array
  const totalBookingsCount =
    equipmentDetails?.metadata?.upcoming_bookings_count ||
    upcomingBookings.reduce((total, dateGroup) => total + dateGroup.bookings.length, 0)

  return {
    // Raw data
    equipmentDetails,
    equipmentData,
    detailsLoading,

    // Processed data
    equipment,
    upcomingBookings,
    totalBookingsCount,

    // Boat data for modal
    boat,
    boatLoading,

    // Deactivation/deletion details
    equipmentDeactivationDetails,
    equipmentDeletionDetails,
    deactivationDetailsLoading,
    deletionDetailsLoading,
    deactivateLoading,
    deleteLoading
  }
}
